import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  ASSIGNMENT,
  CAPTURE,
  DEFAULT_PROPERTY,
  SAVE_CAPTION,
  formatReading,
  nowIso,
  tagGroup,
  uuidv7,
  type Evidence,
  type Media,
} from '@kilroy/shared';

import { db } from '../db/db';
import { useUsers } from '../hooks/useUsers';
import { clearDraft, commit, loadDraft, saveDraft } from '../db/commit';
import { refreshCounts } from '../sync/engine';
import { useSession } from '../app/session';
import { useGps } from '../media/gps';
import { GpsRow } from '../components/capture/GpsRow';
import { ManualPosition, type ManualFix } from '../components/capture/ManualPosition';
import { PhotoWell, TagPicker, VoiceNotes } from '../components/capture/inputs';
import {
  StructuralPanel,
  type StructuralDraft,
} from '../components/capture/StructuralPanel';
import '../components/capture/capture.css';

/* The critical screen.
 *
 * Everything here obeys one rule: the save is unconditional. There is no
 * validation gate that can stop a record reaching the device store, except the
 * one the domain genuinely requires (a structural reading must declare its
 * source). No GPS lock, no photo, no network, no auth — none of them can block
 * a save, because every one of them is a way to lose an observation that
 * already happened.
 *
 * The draft is written on every change, so process death costs nothing.
 */

/** Selected chips, filtered to one vocabulary group. */
const byGroup = (tags: string[], group: string) =>
  tags.filter((t) => tagGroup(t)?.id === group);

const DRAFT_ID = 'capture';

type Mode = 'rock_observation' | 'structural_feature';

interface Draft {
  mode: Mode;
  media: Media[];
  tags: string[];
  notes: string;
  structural: StructuralDraft;
  assignmentId?: string;
}

const emptyDraft = (assignmentId?: string): Draft => ({
  mode: 'rock_observation',
  media: [],
  tags: [],
  notes: '',
  assignmentId,
  structural: {
    feature_type: 'fault',
    strike: 0,
    dip: 45,
    dip_direction: 'E',
    reading_source: null,
    confidence: 'medium',
  },
});

export function Capture() {
  const { userById } = useUsers();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, deviceId } = useSession();
  const assignmentId = params.get('assignment') ?? undefined;

  const [draft, setDraft] = useState<Draft>(() => emptyDraft(assignmentId));
  const [restored, setRestored] = useState(false);
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  const gps = useGps(true);
  /* A typed position overrides the phone's, and records that it did.
   *
   * The team carries handheld GPS units. That makes a typed coordinate the
   * better number more often than not — and with the radio off in a canyon it
   * is frequently the only one. It is optional and never automatic: the phone
   * keeps trying in the background either way. */
  const [manual, setManual] = useState<ManualFix | null>(null);
  const [typing, setTyping] = useState(false);

  const assignment = useLiveQuery(
    () => (assignmentId ? db.assignments.get(assignmentId) : undefined),
    [assignmentId],
  );

  /* Restore a draft that survived a process kill.
     `hydrated` gates the autosave below: without it the two effects race on
     mount, the empty draft is written first, and the screen announces that it
     restored the blank it had just saved. */
  const hydrated = useRef(false);

  useEffect(() => {
    void loadDraft(DRAFT_ID).then((row) => {
      const saved = row?.data as Draft | undefined;
      // Only announce a restore when there is something in it worth restoring.
      const hasContent =
        !!saved &&
        (saved.media?.length > 0 || saved.tags?.length > 0 || !!saved.notes?.trim());
      if (saved) setDraft(saved);
      if (hasContent) setRestored(true);
      hydrated.current = true;
    });
  }, []);

  /* Autosave every change. Cheap, and the alternative is losing a voice note
     because a phone call came in. */
  useEffect(() => {
    if (!hydrated.current) return;
    void saveDraft(DRAFT_ID, draft);
  }, [draft]);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const structuralIncomplete =
    draft.mode === 'structural_feature' && !draft.structural.reading_source;

  const queued = useLiveQuery(() => db.outbox.count(), [], 0);

  const title = useMemo(
    () => (draft.mode === 'structural_feature' ? 'Structural reading' : 'Rock observation'),
    [draft.mode],
  );

  async function save() {
    if (structuralIncomplete) {
      setShowError(true);
      // Send focus to the thing that's missing rather than leaving a gloved
      // thumb to hunt for it.
      document.querySelector<HTMLElement>('.field--error')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    setSaving(true);

    // A typed position wins. It came from a better instrument, and the record
    // says which instrument via position_source.
    const deviceFix = gps.fix ?? gps.best;
    const fix = manual
      ? { lat: manual.lat, lon: manual.lon, accuracy: manual.accuracy, altitude: undefined }
      : deviceFix;
    const id = uuidv7();

    const evidence: Evidence = {
      id,
      project_id: 'kilroy-phase-1',
      evidence_type: draft.mode,
      geometry: {
        type: 'Point',
        // No fix yet? The record still saves. Accuracy carries the truth: a
        // sentinel of -1 says "unknown", which is honest, where 0 would be a lie.
        coordinates: fix ? [fix.lon, fix.lat] : [0, 0],
      },
      gps_accuracy_m: fix ? fix.accuracy : -1,
      altitude_m: fix?.altitude ?? undefined,
      position_source: manual ? manual.source : 'device_gps',
      user_id: user.id,
      captured_at: nowIso(),
      notes_text: draft.notes,
      media: draft.media,
      current_confirmation_status: 'pending',
      current_training_status: 'draft',
      source_assignment_id: draft.assignmentId,
      entry_mode: 'field',
      device_id: deviceId,
      property_id: assignment?.property_id ?? DEFAULT_PROPERTY,
      sync_state: 'local',
      ...(draft.mode === 'rock_observation'
        ? {
            // The chips are one flat selection to the person tapping them, but
            // the PRD has three separate columns. Split by the group each tag
            // came from rather than dumping everything into alteration_type —
            // otherwise a rock type is permanently mislabelled as alteration,
            // and no amount of correcting fixes the schema.
            rock_observation: {
              rock_type: byGroup(draft.tags, 'rock_type')[0] ?? '',
              alteration_type: byGroup(draft.tags, 'alteration'),
              texture_type: byGroup(draft.tags, 'texture')[0] ?? '',
            },
          }
        : {
            structural_feature: {
              feature_type: draft.structural.feature_type,
              strike_dip: formatReading(draft.structural),
              reading: {
                strike: draft.structural.strike,
                dip: draft.structural.dip,
                dip_direction: draft.structural.dip_direction,
              },
              offset_sense: draft.structural.offset_sense,
              confidence: draft.structural.confidence,
              reading_source: draft.structural.reading_source!,
            },
          }),
    };

    // One transaction: the record and every one of its files. Outbox order is
    // preserved, so the server never sees an object without its record.
    await commit(
      { kind: 'evidence', record: evidence },
      ...draft.media.map((m) => ({ kind: 'media' as const, record: { id: m.id } })),
    );

    await clearDraft(DRAFT_ID);
    await refreshCounts();

    navigate('/', {
      replace: true,
      state: { toast: CAPTURE.savedToast(queued + 1 + draft.media.length) },
    });
  }

  return (
    <div className="screen capture">
      <div className="screen__h">
        <h1 className="screen__t">{title}</h1>
        {assignment && (
          <p className="capture__against">
            {ASSIGNMENT.forWhom(
              userById(assignment.created_by).name.split(' ')[0],
              assignment.title,
            )}
          </p>
        )}
        {restored && <p className="screen__s">{CAPTURE.draftRestored}</p>}
      </div>

      {/* Type toggle. Built to extend — soil and trench are post-v1, and
          trench is blocked on the wall_log format the geology team owns. */}
      <div className="modeswitch" role="tablist" aria-label="Evidence type">
        {(
          [
            ['rock_observation', 'Rock / outcrop'],
            ['structural_feature', 'Structural'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={draft.mode === id}
            className="modeswitch__btn"
            onClick={() => set({ mode: id })}
          >
            {label}
          </button>
        ))}
      </div>

      <PhotoWell media={draft.media} onChange={(media) => set({ media })} fix={gps.fix} />

      <GpsRow
        gps={gps}
        manual={manual}
        onType={() => setTyping(true)}
        onClearManual={() => setManual(null)}
      />

      {typing && (
        <ManualPosition
          propertyId={assignment?.property_id ?? DEFAULT_PROPERTY}
          onApply={(fix) => {
            setManual(fix);
            setTyping(false);
          }}
          onCancel={() => setTyping(false)}
        />
      )}

      <VoiceNotes media={draft.media} onChange={(media) => set({ media })} fix={gps.fix} />

      {draft.mode === 'rock_observation' ? (
        <TagPicker selected={draft.tags} onChange={(tags) => set({ tags })} />
      ) : (
        <StructuralPanel
          draft={draft.structural}
          onChange={(structural) => set({ structural })}
          showError={showError}
        />
      )}

      <div className="field">
        <div className="label">Notes</div>
        <textarea
          className="textarea"
          rows={3}
          value={draft.notes}
          placeholder="Anything the photo and the voice note don’t carry."
          onChange={(e) => set({ notes: e.target.value })}
        />
      </div>

      {/* The save.
       *
       * Sticky, because on a long capture the button scrolls away exactly when
       * someone is finished and wants it — and a save they have to hunt for is
       * a save that sometimes does not happen.
       *
       * The caption is the product's whole promise in one line, so it sits with
       * the button rather than in a help screen nobody opens. */}
      <div className="capture__save">
        <button
          type="button"
          className="btn btn--primary btn--lg capture__go"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <p className="capture__caption mono">{SAVE_CAPTION}</p>
      </div>
    </div>
  );
}
